import {
  getRoleLabel,
  type SimpleRole,
  type TeamSpace,
} from "@prismicio/editor-fields"
import {
  ActionListHeader,
  ActionListItem,
  ActionListLabel,
  Box,
  Button,
  IconButton,
  ScrollArea,
} from "@prismicio/editor-ui"
import { useCallback, useLayoutEffect, useRef, useState } from "react"
import { useLocation, useSearchParams } from "react-router-dom"

import { Link } from "../../app/routing/Link"
import { manageTeamSpacePermission } from "../../app/services/permissions"
import { useResolveMyRole, useTeamSpaces } from "../../app/services/role"
import { useDocumentListLocales } from "../useDocumentListLocales"
import { useDocumentListSearchParams } from "../useDocumentListSearchParams"
import { getSearchParamsForTeamSpaceNavigation } from "./TeamSpaces.utils"
import { TeamSpacesDialog } from "./TeamSpacesDialog"
import { useTeamSpaceSettingsDeepLink } from "./useTeamSpacesSettingsDeepLink"

export function TeamSpacesList() {
  const teamSpaces = useTeamSpaces()
  const { locale } = useDocumentListLocales()
  const resolveMyRole = useResolveMyRole()
  const repositoryRole = resolveMyRole({})
  const deepLink = useTeamSpaceSettingsDeepLink()

  const canManageTeamSpace = manageTeamSpacePermission[repositoryRole]

  const [isOpen, setIsOpen] = useState(false)
  const [teamSpace, setTeamSpace] = useState<TeamSpace | undefined>()

  const openSettings = useCallback((teamSpace?: TeamSpace) => {
    setTeamSpace(teamSpace)
    setIsOpen(true)
  }, [])

  deepLink.useOpenSettings(openSettings)

  const teamSpacesListMinHeight = getTeamSpacesMinHeight(teamSpaces?.length)

  return (
    <>
      <ActionListHeader>
        <ActionListLabel>Spaces</ActionListLabel>
        {canManageTeamSpace && (
          <>
            <Button
              invisible
              size="small"
              textWeight="normal"
              onClick={() => openSettings()}
            >
              Create a Space
            </Button>
            <TeamSpacesDialog
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              teamSpace={teamSpace}
            />
          </>
        )}
      </ActionListHeader>
      <Box
        flexDirection="column"
        flexGrow={1}
        flexBasis={0}
        minHeight={teamSpacesListMinHeight}
      >
        <ScrollArea
          style={{
            padding: 1,
            gap: 8,
          }}
          scrollPaddingBlock={1}
          stableScrollbar={false}
        >
          {teamSpaces?.map((teamSpace) => (
            <TeamSpaceItem
              key={teamSpace.id}
              showSettings={canManageTeamSpace}
              onSettingsClick={() => openSettings(teamSpace)}
              role={resolveMyRole({
                localeId: locale,
                teamSpaceId: teamSpace.id,
              })}
              teamSpace={teamSpace}
            />
          ))}
        </ScrollArea>
      </Box>
    </>
  )
}

const minimumTeamSpacesToShow = 3.5

function getTeamSpacesMinHeight(teamSpacesCount?: number) {
  const count = (teamSpacesCount ?? 0) + 1 // +1 for the main space
  return `${
    Math.min(count, minimumTeamSpacesToShow) * 40 + // item height
    Math.min(count - 1, Math.ceil(minimumTeamSpacesToShow - 1)) * 8 + // gaps between items
    2 // 1px padding for ActionListItem's outline
  }px`
}

interface TeamSpaceItemProps {
  role: SimpleRole
  showSettings?: boolean
  teamSpace: TeamSpace
  onSettingsClick?: () => void
}

function TeamSpaceItem(props: TeamSpaceItemProps) {
  const { showSettings, role, teamSpace, onSettingsClick } = props

  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()
  const isWorking = pathname.startsWith("/working")
  const isArchived = pathname.startsWith("/archived")
  const isUpcoming = pathname.startsWith("/upcoming")
  const isWorkingOrArchived = isWorking || isArchived

  const pathnameToGo = isWorkingOrArchived ? "." : "/working"

  const { optimisticSearchParams, searchAction } = useDocumentListSearchParams()

  const params = getSearchParamsForTeamSpaceNavigation(
    searchParams,
    teamSpace.id,
    teamSpace.customTypes,
  )

  const disabled = isUpcoming
  const selected =
    isWorkingOrArchived && optimisticSearchParams.space === teamSpace.id

  const itemRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (selected)
      itemRef.current?.scrollIntoView({
        block: "nearest",
      })
  }, [selected])

  return (
    <ActionListItem
      ref={itemRef}
      variant="compact"
      textVariant="normal"
      backgroundColor="transparent"
      startIcon="folder"
      selected={selected}
      disabled={disabled}
      endOverlay={
        showSettings &&
        !disabled && (
          <IconButton
            icon="tune"
            variant="ghost"
            size="small"
            color={selected ? "purple" : "darkGrey"}
            onClick={onSettingsClick}
            hiddenLabel={`${teamSpace.name} settings`}
          />
        )
      }
      onClick={(e) => {
        /**
         * on click with cmd/ctrl or from outside of Team Spaces - perform native navigation
         * on click - bypass native navigation, update state + query params manually
         */
        if (e.metaKey || e.ctrlKey || !isWorkingOrArchived) return

        e.preventDefault()
        searchAction.setSearch(() => params)
      }}
      description={getRoleLabel(role)}
      oneLine
      noWrapTitle
      asChild
    >
      <Link
        to={{
          pathname: pathnameToGo,
          search: params.toString(),
        }}
        disabled={disabled}
        title={teamSpace.name}
      >
        {teamSpace.name}
      </Link>
    </ActionListItem>
  )
}
